import kleur from 'kleur';

interface RateLimitConfig {
  tokensPerMinute: number;
  requestsPerMinute: number;
  burstAllowance: number; // Allow some burst capacity
}

interface RequestRecord {
  timestamp: number;
  tokens: number;
}

// Rate limit configurations for different Groq models
const GROQ_RATE_LIMITS: Record<string, RateLimitConfig> = {
  'openai/gpt-oss-20b': {
    tokensPerMinute: 250000,
    requestsPerMinute: 30,
    burstAllowance: 0.8 // Use 80% of limit to be safe
  },
  'llama-3.1-70b-versatile': {
    tokensPerMinute: 6000,
    requestsPerMinute: 30,
    burstAllowance: 0.8
  },
  'llama-3.1-8b-instant': {
    tokensPerMinute: 30000,
    requestsPerMinute: 30,
    burstAllowance: 0.8
  },
  'mixtral-8x7b-32768': {
    tokensPerMinute: 5000,
    requestsPerMinute: 30,
    burstAllowance: 0.8
  },
  'gemma-7b-it': {
    tokensPerMinute: 30000,
    requestsPerMinute: 30,
    burstAllowance: 0.8
  }
};

export class GroqRateLimiter {
  private requestHistory: RequestRecord[] = [];
  private config: RateLimitConfig;
  private modelName: string;

  constructor(modelName: string) {
    this.modelName = modelName;
    this.config = GROQ_RATE_LIMITS[modelName] || {
      tokensPerMinute: 5000, // Conservative default
      requestsPerMinute: 30,
      burstAllowance: 0.8
    };
  }

  /**
   * Check if we should delay before making a request
   * Returns delay in milliseconds (0 if no delay needed)
   */
  async shouldDelay(estimatedTokens: number): Promise<number> {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;

    // Clean old requests (older than 1 minute)
    this.requestHistory = this.requestHistory.filter(
      record => record.timestamp > oneMinuteAgo
    );

    // Calculate current usage
    const recentTokens = this.requestHistory.reduce((sum, record) => sum + record.tokens, 0);
    const recentRequests = this.requestHistory.length;

    // Check if adding this request would exceed limits
    const tokenLimit = this.config.tokensPerMinute * this.config.burstAllowance;
    const requestLimit = this.config.requestsPerMinute * this.config.burstAllowance;

    const wouldExceedTokens = (recentTokens + estimatedTokens) > tokenLimit;
    const wouldExceedRequests = (recentRequests + 1) > requestLimit;

    if (wouldExceedTokens || wouldExceedRequests) {
      // Calculate delay needed
      let delay = 0;

      if (wouldExceedTokens && this.requestHistory.length > 0) {
        // Find the oldest request that puts us over the limit
        const oldestRelevantRequest = this.requestHistory[0];
        delay = Math.max(delay, (oldestRelevantRequest.timestamp + 60000) - now);
      }

      if (wouldExceedRequests && this.requestHistory.length > 0) {
        const oldestRequest = this.requestHistory[0];
        delay = Math.max(delay, (oldestRequest.timestamp + 60000) - now);
      }

      // Add a small buffer to be safe
      delay = Math.max(delay + 1000, 2000); // Minimum 2 second delay

      return delay;
    }

    // Add minimum delay between requests for Groq (they're very fast)
    const lastRequest = this.requestHistory[this.requestHistory.length - 1];
    if (lastRequest && (now - lastRequest.timestamp) < 100) {
      return 100; // 100ms minimum between requests
    }

    return 0;
  }

  /**
   * Record a completed request
   */
  recordRequest(actualTokens: number): void {
    this.requestHistory.push({
      timestamp: Date.now(),
      tokens: actualTokens
    });
  }

  /**
   * Display a user-friendly delay message
   */
  async delayWithMessage(delayMs: number): Promise<void> {
    if (delayMs <= 0) return;

    const seconds = Math.ceil(delayMs / 1000);
    const reason = this.getDelayReason();

    // Show a Claude Code style delay message
    process.stdout.write(kleur.yellow(`⏳ Rate limiting: waiting ${seconds}s ${reason}\r`));

    await new Promise(resolve => setTimeout(resolve, delayMs));

    // Clear the delay message
    process.stdout.write(' '.repeat(80) + '\r');
  }

  private getDelayReason(): string {
    const oneMinuteAgo = Date.now() - 60000;
    const recentTokens = this.requestHistory
      .filter(r => r.timestamp > oneMinuteAgo)
      .reduce((sum, record) => sum + record.tokens, 0);

    const tokenUsagePercent = Math.round((recentTokens / this.config.tokensPerMinute) * 100);

    return `(${tokenUsagePercent}% of token limit used)`;
  }

  /**
   * Get current usage statistics
   */
  getUsageStats(): { tokens: number; requests: number; tokensPercent: number; requestsPercent: number } {
    const oneMinuteAgo = Date.now() - 60000;
    const recentRequests = this.requestHistory.filter(r => r.timestamp > oneMinuteAgo);

    const tokens = recentRequests.reduce((sum, record) => sum + record.tokens, 0);
    const requests = recentRequests.length;

    return {
      tokens,
      requests,
      tokensPercent: Math.round((tokens / this.config.tokensPerMinute) * 100),
      requestsPercent: Math.round((requests / this.config.requestsPerMinute) * 100)
    };
  }

  /**
   * Estimate tokens from message content (rough approximation)
   */
  static estimateTokens(messages: any[], tools?: any[]): number {
    let estimate = 0;

    // Estimate tokens from messages (roughly 4 chars per token)
    for (const message of messages) {
      if (message.content) {
        estimate += Math.ceil(message.content.length / 4);
      }
    }

    // Add tool definitions
    if (tools && tools.length > 0) {
      const toolsText = JSON.stringify(tools);
      estimate += Math.ceil(toolsText.length / 4);
    }

    // Add buffer for response tokens (conservative estimate)
    estimate += 1000;

    return estimate;
  }
}